import { TypedHtmlElements } from "@cervval.js/client-html5-core/src/components/typedHtmlElements.decorator";
import { Display, toggleVisible } from "@cervval.js/client-html5-core/src/tools/css.tools";
import { FLEX_CONTAINER_COLUMN, FLEX_ITEM, H_100, PADDING, W_100 } from "@cervval.js/client-html5-core/src/tools/cssClasses.tools";
import { CustomElement, NAVIGATION_SERVICE, NavigationService, WebPageComponent } from "@cervval.js/client-html5-spa/navigation";
import { CjsButton } from "@cervval.js/client-html5-ui/src/components/button/button";
import { CjsGeneratedForm } from "@cervval.js/client-html5-ui/src/components/generatedForm/generatedForm";
import { GeneratedFormEditMode, GeneratedFormSaveMode } from "@cervval.js/client-html5-ui/src/components/generatedForm/generatedForm.types";
import { CatchErrorWithDialog } from "@cervval.js/client-html5-ui/src/tools/catchErrorWithDialog.decorator";
import { Injected } from "@cervval.js/common/src/dependencies/injected";
import { DataModel } from "@cervval.js/common/src/forms/dataModel";
import { getLogConsole } from "@cervval.js/common/src/services/log.service";
import { keyof } from "@cervval.js/common/src/tools/object.tools";
import { formatMinutesHHMM } from "@cervval.js/common/src/tools/time.tools";
import { durationValue } from "@cervval.js/common/src/tools/timestamps.tools";
import { UnixTimestamp } from "@cervval.js/common/src/tools/timestamps.tools.definitions";
import { translateHTMLReferences } from "@ehealth_tools/client-html-tools/src/tools/webcomponent.tools";
import { getProductOverridePropertyConfig } from "@ehealth_tools/common-booking/src/configs/productOverride.propertyConfig";
import { getVisioModalityPropertyConfig, VisioModality } from "@ehealth_tools/common-booking/src/configs/visio.propertyConfig";
import { BOOKING_MODALITY } from "@ehealth_tools/common-booking/src/data/booking.ds";
import { BookingCommonUser, BookingProduct, BookingProfessional, BookingSession } from "@ehealth_tools/common-booking/src/data/booking.dto";
import { isProfessional } from "@ehealth_tools/common-booking/src/data/booking.tools";
import { BOOKING_SERVICE } from "@ehealth_tools/common-booking/src/services/abstractCommonBooking.service";
import { BookingSlider } from "../../../components/bookingSlider/bookingSlider";
import { BookingUserCard } from "../../../components/bookingUserCard/bookingUserCard";
import { BookingPageName } from "../../../modules/booking.pages";
import { AbstractClientBookingService } from "../../../services/abstractClientBooking.service";
import { _I18nBookingPage } from "./bookingPage.i18n";

type ProductOverride = Partial<Pick<BookingSession, "price" | "duration" | "description">>

@CustomElement({
    name: "booking-page",
    style: require("./bookingPage.scss"),
    template: require("./bookingPage.html"),
    i18n: require("./bookingPage.i18n").I18nBookingPage(),
    elementCssClasses: [W_100, H_100, FLEX_CONTAINER_COLUMN, FLEX_ITEM, PADDING.PY_2]
})
export class BookingPage<I18n extends _I18nBookingPage = _I18nBookingPage> extends WebPageComponent<I18n> {
    @TypedHtmlElements({ ensureLoadedClasses: [BookingSlider, CjsGeneratedForm, BookingUserCard] })
    public readonly components!: {
        bookingText: HTMLDivElement,
        bkSlider: BookingSlider,
        bookNow: CjsButton,
        // Div reserved for pro
        professionalArea: HTMLDivElement,
        overriders: CjsGeneratedForm<ProductOverride>,
        // Modality
        visio: CjsGeneratedForm<VisioModality>,
        userCard: BookingUserCard,

        productNameTitle: HTMLDivElement,
        productName: HTMLDivElement,
        productDurationTitle: HTMLDivElement,
        productDuration: HTMLDivElement,
        productPriceTitle: HTMLDivElement,
        productPrice: HTMLDivElement
    };

    @Injected(NAVIGATION_SERVICE)
    private readonly navigationService!: NavigationService<BookingPageName>;

    @Injected(BOOKING_SERVICE)
    private _bookingService!: AbstractClientBookingService;

    private _pro: BookingProfessional | undefined;
    private _customer: BookingCommonUser | undefined;


    /** @inheritdoc */
    public override init(): void {
        translateHTMLReferences(this.components, this.i18n.html);
        this.components.bookNow.options.setAndRefreshIfChanged({
            label: this.i18n.bookNow,
            cssClasses: ["bookNow"]
        });

        this.components.bookNow.clickEvent.on(() => {

            if (this._isValid(this._bookingService)) {
                this.components.overriders.getDataToSave().then(async dataList => {
                    if (this._isValid(this._bookingService) && this._pro && this._customer) {
                        const data = dataList[0];
                        await this._bookingService.book(
                            {
                                professionalId: this._pro.id,
                                date: this._bookingService.bookingDate,
                                productId: this._bookingService.bookingProduct.id,
                                customerIds: [this._customer.id]
                            },
                            {
                                description: data?.description,
                                duration: data?.duration ? data.duration : undefined,
                                price: data?.price
                            }

                        );
                        this.navigationService.navigate("agenda");
                    }
                }).catch(CatchErrorWithDialog);

            }
        });

        this.components.overriders.options.setAndRefreshIfChanged({
            formMode: {
                editMode: GeneratedFormEditMode.CREATE,
                saveMode: GeneratedFormSaveMode.STATIC
            }
        });

    }

    /** @inheritdoc */
    public override refresh(): void {
        if (this._bookingService.userToInteractWith == null) { return; }
        this._pro = isProfessional(this._bookingService.userToInteractWith) ? this._bookingService.userToInteractWith : this._bookingService.getCurrentUser() as BookingProfessional;
        if (!isProfessional(this._pro)) { getLogConsole().error("None of the part is pro"); return; }
        this._customer = isProfessional(this._bookingService.userToInteractWith) ? this._bookingService.getCurrentUser() : this._bookingService.userToInteractWith;
        if (this._pro.id === this._customer.id) {
            getLogConsole().warn("Self booking... why not...");
        }

        this.components.bookingText.innerHTML = this.i18n.bookingText;

        this._refreshSlider();
        this.components.bookNow.options.setAndRefreshIfChanged({
            disabled: !this._isValid(this._bookingService)
        });

        const currentUser = this._bookingService.getCurrentUser();
        toggleVisible(this.components.professionalArea, isProfessional(currentUser), Display.FLEX);

        this.components.overriders.initialize(getProductOverridePropertyConfig(), new DataModel(), [
            {
                price: this._bookingService.bookingProduct?.price,
                duration: this._bookingService.bookingProduct?.duration ? this._bookingService.bookingProduct.duration : undefined,
                description: this._bookingService.bookingProduct?.description
            }
        ]);
        this.components.overriders.editorValueChangeEvent.on((e) => {
            if (e.detail.propertyConfig.propertyName === keyof<ProductOverride>("duration") && typeof e.detail.value === "number") {
                this._refreshSlider(e.detail.value);
            }
        });

        this.components.visio.options.setAndRefreshIfChanged({
            formMode: {
                editMode: GeneratedFormEditMode.CREATE,
                saveMode: GeneratedFormSaveMode.STATIC
            }
        });

        this.components.visio.initialize(getVisioModalityPropertyConfig(), new DataModel(), [{ _modality: BOOKING_MODALITY.LOCAL }]);

        this.components.userCard.options.setAndRefreshIfChanged({
            user: this._pro
        });

        // Product
        const product = this._bookingService.bookingProduct;
        this.components.productName.innerHTML = product?.name ?? "";
        this.components.productDuration.innerHTML = product ? formatMinutesHHMM(durationValue({ second: product.duration }, "minute")) : "";
        this.components.productPrice.innerHTML = (product?.price ?? "") + "€";
    }

    /** Check that all infos for booking are valid */
    private _isValid(bookingService: Pick<AbstractClientBookingService, "userToInteractWith" | "bookingProduct" | "bookingDate">): bookingService is ({ userToInteractWith: BookingProfessional, bookingProduct: BookingProduct, bookingDate: UnixTimestamp }) {
        return this._bookingService.userToInteractWith != null && this._bookingService.bookingProduct != null && this._bookingService.bookingDate != null;
    }

    /** */
    private _refreshSlider(duration?: number): void {
        if (this._pro == null) { return; }
        this.components.bkSlider.options.setAndRefreshIfChanged({
            from: this._bookingService.bookingDate,
            selected: this._bookingService.bookingDate,
            slotDuration: duration ?? this._bookingService.bookingProduct?.duration,
            onSelect: (ts) => this._bookingService.bookingDate = ts,
            professional: this._pro
        });
    }

}
